<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    options: {
      label: string
      value: string | number
    }[]
    modelValue?: string | number
    placeholder?: string
    disabled?: boolean
    id: string
  }>(),
  {
    modelValue: undefined,
    placeholder: undefined,
    disabled: () => false
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void
  (e: 'change', value: string | number | null): void
}>()

const comboboxElement = ref<HTMLElement | null>(null)
const dropdown = ref<HTMLElement | null>(null)
const input = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const highlightedOption = ref<string | number>('')

let searchBuffer = ''
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const selectedOptionLabel = computed(() => props.options.find(option => option.value === props.modelValue)?.label)

watch(
  () => props.options,
  options => {
    if (options.length === 0) {
      updateValue('')
      return
    }

    if (!!props.modelValue && !containsOption(options.find(o => o.value === props.modelValue)?.label || '')) {
      updateValue(options[0].value)
    }
  }
)

watch(
  () => props.modelValue,
  modelValue => {
    if (modelValue) {
      updateValue(modelValue)
    }
  }
)

function keyDown(e: KeyboardEvent) {
  if (/^\S$/.test(e.key)) {
    searchBuffer += e.key
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    searchTimeout = setTimeout(() => (searchBuffer = ''), 1000)

    const searchResult = props.options.find(option => option.label.toLowerCase().startsWith(searchBuffer.toLowerCase()))

    if (!searchResult) {
      return
    }
    if (isOpen.value) {
      highlightedOption.value = searchResult.value
    } else {
      updateValue(searchResult.value)
    }
  }
}

function containsOption(label: string): boolean {
  return props.options.some(option => option.label === label)
}

function toggleOpen(): void {
  if (isOpen.value && highlightedOption.value) {
    updateValue(highlightedOption.value)
  }

  checkExpansionDirection()
  isOpen.value = !isOpen.value
}

function arrowDown() {
  const index = props.options.map(option => option.value).indexOf(highlightedOption.value)
  highlightedOption.value = props.options[Math.min(index + 1, props.options.length - 1)].value

  if (!isOpen.value && highlightedOption.value) {
    updateValue(highlightedOption.value)
  }
}

function arrowUp() {
  const index = props.options.map(option => option.value).indexOf(highlightedOption.value)
  highlightedOption.value = props.options[Math.max(index - 1, 0)].value

  if (!isOpen.value && highlightedOption.value) {
    updateValue(highlightedOption.value)
  }
}

function updateValue(value: string | number) {
  emit('update:modelValue', value)
  emit('change', value)
  nextTick(() => {
    //Next tick to dispatch event after input value got updated for validation
    input.value?.dispatchEvent(new Event('change')) //Event to trigger validation
  })
}

function selectOption(value: string | number): void {
  updateValue(value)
  isOpen.value = false

  dropdown.value?.classList.remove('expand-down', 'expand-up')
}

function onBlur(e: FocusEvent): void {
  if (comboboxElement.value?.contains(e.relatedTarget as HTMLElement)) {
    return
  }
  if (e.target === e.relatedTarget) {
    return
  }
  isOpen.value = false

  dropdown.value?.classList.remove('expand-down', 'expand-up')
}

function checkExpansionDirection(): void {
  const el = dropdown.value as HTMLElement | null

  if (el === null) {
    return
  }

  el.classList.remove('expand-down', 'expand-up')

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (isOpen.value) {
          if (entry.intersectionRatio < 1) {
            entry.target.classList.add('expand-up')
          } else {
            entry.target.classList.add('expand-down')
          }
        }
      })

      observer.unobserve(el)
    },
    {
      rootMargin: '0px 0px -80px 0px',
      threshold: 1
    }
  )
  observer.observe(el)
}
</script>

<template>
  <div ref="comboboxElement" :class="{ open: isOpen }" class="combo-box">
    <button
      :class="{ disabled: disabled }"
      :disabled="disabled || options.length === 0"
      class="combo-box-btn"
      type="button"
      @click="toggleOpen"
      @focusout="onBlur"
      @keydown="keyDown"
      @keydown.esc="onBlur"
      @keydown.tab="onBlur"
      @keydown.prevent.enter="toggleOpen"
      @keydown.prevent.down="arrowDown"
      @keydown.prevent.up="arrowUp"
    >
      <template v-if="modelValue">
        {{ selectedOptionLabel }}
      </template>
      <template v-else>
        {{ placeholder || $t('global.select_placeholder') }}
      </template>
    </button>

    <input :id="id" ref="input" :value="modelValue" type="hidden"><!-- This is only used for validation -->

    <ul v-show="isOpen" ref="dropdown" class="combo-box-list">
      <li
        v-for="option in options"
        :key="`${option.label}_${option.value}`"
        :class="{
          active: modelValue === option.value,
          highlight: highlightedOption === option.value
        }"
        class="combo-box-list-item"
        tabindex="0"
        @click="selectOption(option.value)"
      >
        {{ option.label }}
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/variables';
@import '@/assets/style/helpers/mixins';
@import '@/assets/style/helpers/include-media';

.combo-box {
  width: 100%;
  flex-basis: 100%;
  position: relative;

  &-btn {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    font-size: rem-calc($textSize);
    text-align: left;
    width: 100%;
    height: rem-calc($inputHeight);
    border: 1px solid $borderColor;
    background: none;
    padding: 0 rem-calc(33) 0 rem-calc(12);

    @include themify($themes) {
      border-radius: rem-calc(themed('inputBorderRadius'));
      color: themed('primaryColor');
    }

    @include media('>=tablet') {
      max-width: 400px;
    }

    &:before {
      right: 0;
      position: absolute;
      padding-right: rem-calc(5);
      top: rem-calc(5);
      font-size: rem-calc(28);
      transition: transform $animTimeShort ease;

      @include iconChevronDown;
      @include themify($themes) {
        color: themed('primaryColor');
      }
    }

    .open & {
      border-color: $textColor;

      &:before {
        transform: rotate3d(1, 0, 0, 180deg);
      }
    }

    &.disabled {
      opacity: 0.3;
    }

    .is--invalid & {
      color: $errorColor;
      border-color: $errorColor;
      background-color: $errorBgColor;
    }
  }

  &-list {
    visibility: hidden;
    position: absolute;
    width: 100%;
    z-index: 3;
    max-height: rem-calc(240);
    max-width: rem-calc(384);
    background: #fff;
    overflow-y: auto;
    overflow-x: hidden;

    @include componentShadow();
    @include themify($themes) {
      border-radius: rem-calc(themed('inputBorderRadius'));
    }

    &.expand-up {
      bottom: 100%;
      visibility: visible;
    }

    &.expand-down {
      top: 100%;
      visibility: visible;
    }

    &-item {
      cursor: pointer;
      line-height: rem-calc(24);
      color: $textColor;
      padding: rem-calc(8) rem-calc(12);

      &:hover,
      &.highlight {
        @include themify($themes) {
          background: themed('selectHoverBg');
        }
      }

      &.active {
        @include themify($themes) {
          background: rgba(themed('primaryColor'), 0.1);
        }

        &:before {
          position: absolute;
          right: rem-calc(8);
          font-size: rem-calc(26);

          @include iconCheck;
          @include themify($themes) {
            color: themed('primaryColor');
          }
        }
      }
    }
  }
}
</style>
